import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  DURATION_ALL_VALUE,
  DURATION_FILTER_OPTIONS,
  type DurationRange,
} from '@/utils/topic-duration';
import {
  CONTENT_CATEGORY_LABELS,
  type ContentCategory,
} from '@shared/api.interface';
import { TOPIC_FILTER_LABELS } from './topic-filter-copy';
import { CategoryFilterIcon, DurationDialIcon } from './topic-filter-icons';

interface TopicFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 点“看这 N 个主题”：不传时只关闭弹层 */
  onSeeResults?: () => void;
  durationRanges: DurationRange[];
  category: ContentCategory | '';
  resultCount: number;
  onDurationToggle: (value: DurationRange | typeof DURATION_ALL_VALUE) => void;
  onCategoryChange: (value: ContentCategory | '') => void;
  onClear: () => void;
  /** 各分类在其它筛选条件下的主题数；'' 表示全部 */
  categoryCounts?: Partial<Record<ContentCategory | '', number>>;
  /** 已登录时显示“只看未领取”开关 */
  showUnclaimedToggle?: boolean;
  unclaimedOnly?: boolean;
  onUnclaimedOnlyChange?: (value: boolean) => void;
}

// 柔色块按钮，和电脑左侧筛选栏一致；dim：当前条件下这一项没有主题
const optionClass = (active: boolean, dim = false) =>
  `inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-2 text-sm transition-colors ${
    active
      ? 'bg-white font-semibold text-orange-800 shadow-[inset_0_0_0_1.5px_var(--cta)]'
      : 'bg-[#F7F2EA] font-medium text-[#3A2F25]'
  } ${dim && !active ? 'opacity-45' : ''}`;

export function TopicFilterDrawer({
  open,
  onOpenChange,
  onSeeResults,
  durationRanges,
  category,
  resultCount,
  onDurationToggle,
  onCategoryChange,
  onClear,
  categoryCounts,
  showUnclaimedToggle = false,
  unclaimedOnly = false,
  onUnclaimedOnlyChange,
}: TopicFilterDrawerProps) {
  const hasActiveFilters = durationRanges.length > 0 || !!category || unclaimedOnly;
  const isEmptyCategory = (key: ContentCategory) => categoryCounts?.[key] === 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[86vh] rounded-t-3xl">
        <DrawerHeader className="border-b border-border px-5 pb-3 pt-4 text-left">
          <DrawerTitle className="text-xl font-semibold">改一下条件</DrawerTitle>
          <DrawerDescription className="sr-only">按题材和时长筛选</DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-6 px-5 py-5">
            {showUnclaimedToggle && onUnclaimedOnlyChange && (
              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl bg-background px-4 py-3.5">
                <span>
                  <span className="block text-[15px] font-semibold text-foreground">只看未领取</span>
                  <span className="block text-xs text-muted-foreground">已经领过的主题先藏起来，只看还没领的</span>
                </span>
                <Switch
                  checked={unclaimedOnly}
                  onCheckedChange={onUnclaimedOnlyChange}
                  aria-label="只看未领取"
                  className="data-[state=checked]:bg-cta"
                />
              </label>
            )}
            <section>
              <h3 className="mb-3 text-[15px] font-semibold text-foreground">{TOPIC_FILTER_LABELS.category}</h3>
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => onCategoryChange('')} className={optionClass(category === '')}>
                  全部
                </button>
                {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onCategoryChange(key as ContentCategory)}
                    className={optionClass(category === key, isEmptyCategory(key as ContentCategory))}
                  >
                    <CategoryFilterIcon category={key} />
                    {label}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-[15px] font-semibold text-foreground">
                {TOPIC_FILTER_LABELS.duration}
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">{TOPIC_FILTER_LABELS.durationHint}</span>
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {DURATION_FILTER_OPTIONS.map((option) => {
                  const isActive =
                    option.value === DURATION_ALL_VALUE
                      ? durationRanges.length === 0
                      : durationRanges.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onDurationToggle(option.value)}
                      className={`${optionClass(isActive)} ${
                        option.value === DURATION_ALL_VALUE ? 'col-span-2' : '!justify-start gap-2 !pl-4'
                      }`}
                    >
                      {option.value === DURATION_ALL_VALUE ? (
                        '时长不限'
                      ) : (
                        <>
                          <DurationDialIcon range={option.value} />
                          {option.label}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

          </div>
        </ScrollArea>

        <DrawerFooter className="grid grid-cols-[1fr_2fr] gap-2.5 border-t border-border bg-white px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClear}
            disabled={!hasActiveFilters}
            className="h-12 rounded-xl border-orange-200 text-[15px] font-semibold text-orange-800 hover:bg-orange-50"
          >
            清空
          </Button>
          <Button
            type="button"
            onClick={() => (onSeeResults ? onSeeResults() : onOpenChange(false))}
            className="h-12 rounded-xl bg-cta text-[15px] font-semibold text-cta-foreground hover:bg-cta-hover"
          >
            看这 {resultCount} 个主题
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
